import { Typography } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import React, { useMemo } from "react";

import { activityApi } from "apis";
import { getMetricsByCategory } from "constants/metrics";
import {
  MetricValueAggregatedResponseDto,
  MetricValuesAggregationRequestDto,
  MetricValuesAggregationRequestDtoIntervalEnum,
  MetricValuesAggregationRequestDtoAggregateTypesEnum,
  MetricValuesAggregationRequestDtoMetricCodeEnum,
  MetricValuesAggregationRequestDtoHealthProviderCodeEnum,
} from "libs/cellera-sdk";
import MetricCard, { MetricData } from "./MetricCard";
import SectionWrapper from "./SectionWrapper";

// Get activity metrics from centralized configuration
const ACTIVITY_METRICS_MAP = getMetricsByCategory("activity");

type ActivityMetricCode = keyof typeof ACTIVITY_METRICS_MAP;

interface ActivitiesProps {
  patientId: string;
  onViewHistory: () => void;
}

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "No Data";

  // Handle numeric values
  if (typeof value === "number") {
    return Math.round(value).toString();
  }

  // Handle workout data (might be JSON format with sessions and time)
  if (typeof value === "object" && value !== null) {
    const workout = value as { sessions?: number; totalTime?: number };
    if (typeof workout.sessions === "number") {
      return workout.sessions.toString();
    }
    return "No Data";
  }

  return "No Data";
};

const getFallbackInfo = (): MetricData["fallbackInfo"] => ({
  message: "No activity data available",
  suggestion: "Sync with health app or manual entry",
  lastRecorded: "Not available",
});

// Static activity queries configuration - moved outside component for better performance
const activityQueries = [
  {
    queryKey: "step_counts",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.StepCounts,
    aggregateType: MetricValuesAggregationRequestDtoAggregateTypesEnum.Sum,
  },
  {
    queryKey: "distance",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.Distance,
    aggregateType: MetricValuesAggregationRequestDtoAggregateTypesEnum.Sum,
  },
  {
    queryKey: "active_calories",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.ActiveCalories,
    aggregateType: MetricValuesAggregationRequestDtoAggregateTypesEnum.Sum,
  },
  {
    queryKey: "workouts",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.Workouts,
    aggregateType: MetricValuesAggregationRequestDtoAggregateTypesEnum.Sum,
  },
  {
    queryKey: "floor_climbed",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.FloorClimbed,
    aggregateType: MetricValuesAggregationRequestDtoAggregateTypesEnum.Sum,
  },
];

const Activities: React.FC<ActivitiesProps> = ({
  patientId,
  onViewHistory,
}) => {
  // Memoize today's date range to avoid recreating on every render
  const dateRange = useMemo(() => {
    const today = new Date();
    const startOfDay = new Date(today);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);
    return {
      startDate: startOfDay.toISOString(),
      endDate: endOfDay.toISOString(),
    };
  }, []);

  // Memoize query configuration to prevent recreation
  const queryConfig = useMemo(
    () => ({
      enabled: !!patientId,
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }),
    [patientId],
  );

  const [
    { data: stepsData, isLoading: stepsLoading },
    { data: distanceData, isLoading: distanceLoading },
    { data: caloriesData, isLoading: caloriesLoading },
    { data: workoutsData, isLoading: workoutsLoading },
    { data: floorsData, isLoading: floorsLoading },
  ] = useQueries({
    queries: activityQueries.map((query) => ({
      queryKey: ["activities", patientId, query.queryKey],
      queryFn: () => {
        if (!patientId) return null;

        const request: MetricValuesAggregationRequestDto = {
          metricCode: query.metricCode,
          healthProviderCode:
            MetricValuesAggregationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
          interval: MetricValuesAggregationRequestDtoIntervalEnum.Daily,
          aggregateTypes: [query.aggregateType],
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          orderBy: "DESC",
        };

        return activityApi.getMetricValuesAggregatedByPatientId({
          patientId,
          metricValuesAggregationRequestDto: request,
        });
      },
      ...queryConfig,
    })),
  });

  // Memoize metric data mapping to prevent recreation
  const metricDataMap = useMemo(
    (): Record<string, MetricValueAggregatedResponseDto | undefined> => ({
      [MetricValuesAggregationRequestDtoMetricCodeEnum.StepCounts]:
        stepsData?.data[0],
      [MetricValuesAggregationRequestDtoMetricCodeEnum.Distance]:
        distanceData?.data[0],
      [MetricValuesAggregationRequestDtoMetricCodeEnum.ActiveCalories]:
        caloriesData?.data[0],
      [MetricValuesAggregationRequestDtoMetricCodeEnum.Workouts]:
        workoutsData?.data[0],
      [MetricValuesAggregationRequestDtoMetricCodeEnum.FloorClimbed]:
        floorsData?.data[0],
    }),
    [stepsData, distanceData, caloriesData, workoutsData, floorsData],
  );

  // Memoize display data to prevent recalculation on every render
  const displayData = useMemo(
    (): MetricData[] =>
      Object.keys(ACTIVITY_METRICS_MAP).map((metricCode) => {
        const metricInfo =
          ACTIVITY_METRICS_MAP[metricCode as ActivityMetricCode];
        const latestValue = metricDataMap[metricCode];

        if (!latestValue) {
          return {
            id: metricCode,
            name: metricInfo.name,
            value: "No Data",
            unit: metricInfo.unit,
            lastChecked: "Never",
            hasData: false,
            fallbackInfo: getFallbackInfo(),
          };
        }

        const formattedValue = formatValue(
          latestValue.valueJsonData?.sum ??
            latestValue.valueIntData?.sum ??
            latestValue.valueFloatData?.sum,
        );

        const isNoData = formattedValue === "No Data" || formattedValue === "0";

        return {
          id: metricCode,
          name: metricInfo.name,
          value: formattedValue,
          unit: metricInfo.unit,
          lastChecked: new Date(latestValue.startDate).toLocaleString("vi-VN"),
          hasData: !isNoData,
          fallbackInfo: isNoData ? getFallbackInfo() : undefined,
        };
      }),
    [metricDataMap],
  );

  // Memoize loading state calculation
  const isLoading = useMemo(
    () =>
      stepsLoading ||
      distanceLoading ||
      caloriesLoading ||
      workoutsLoading ||
      floorsLoading,
    [
      stepsLoading,
      distanceLoading,
      caloriesLoading,
      workoutsLoading,
      floorsLoading,
    ],
  );

  if (isLoading) {
    return (
      <SectionWrapper title="Activities" onViewAll={onViewHistory}>
        <Typography>Loading activity data...</Typography>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Activities"
      onViewAll={onViewHistory}
      spacing="compact"
    >
      {displayData.map((metric) => (
        <MetricCard key={metric.id} metric={metric} />
      ))}
    </SectionWrapper>
  );
};

export default Activities;
